// 剣舞の「引き寄せ」(開発版 ?bladePull=1・技の中身は src/blade_dancer_pull.ts) の布を描く。
//   render.ts から呼ぶので render.ts を読み込まない (循環を作らない)。技が入っていない時は何も描かない。
import { phaseProgressOf } from './boss_pose_timeline'
import { SASH_STRIP, SASH_TIPS, bladeFxRestImg, bladeSashPaintOn, bladeSashWidth } from './blade_dancer_fx_rest'
import type { Boss, Player } from './types'

const BOSS_ID = 'blade-dancer'
export const BLADE_PULL_ID = 'bladeRibbon'
export const THROW_AT = 0.8                 // 溜めのこの所から布が飛ぶ
export const RIBBON_REACH_PX = 440

// ---- 布 ----
const HAND_THROW = { fwd: 124, up: 118 }   // c2 の差し伸べた手
const HAND_PULL = { fwd: 43, up: 96 }       // c3 の引き戻した拳
const CHEST_UP_PX = 70                      // 主人公の胸 (足元から)
const RETRACT_S = 0.2
const RIBBON_WIDTH_PX = 7
const RIBBON_EDGE = 'rgba(176, 150, 226, 0.95)'
const RIBBON_CORE = 'rgba(244, 238, 255, 0.95)'
const RIBBON_GLOW = 'rgba(190, 160, 255, 0.45)'

type Pt = { x: number; y: number }
// 手から離れた布の先の最後の位置と、戻し始めた時刻 (外れた時・放した後に手へ戻す)。
let ribbon: { end: Pt; retractFrom: number } | null = null
let lastTime = -1

/** 布の端から端までの位置を返す (描かない時は null)。テストからも呼ぶ。 */
export function bladePullRibbonEnds(boss: Boss, player: Pick<Player, 'x' | 'y' | 'state'>, footY: number, time: number): { hand: Pt; end: Pt; taut: number } | null {
  if (boss.id !== BOSS_ID || boss.currentAttack?.id !== BLADE_PULL_ID) { ribbon = null; return null }
  if (time < lastTime) ribbon = null
  lastTime = time
  const dir = boss.facing === 'right' ? 1 : -1
  const at = (p: { fwd: number; up: number }): Pt => ({ x: boss.x + dir * p.fwd, y: footY - p.up })
  const { phase, progress, hitIndex } = phaseProgressOf(boss)
  const grabbed = player.state === 'grabbed'
  const chest = { x: player.x, y: player.y - CHEST_UP_PX }
  if (phase === 'telegraph') {
    if (progress < THROW_AT) { ribbon = null; return null }
    const hand = at(HAND_THROW)
    const k = (progress - THROW_AT) / (1 - THROW_AT)
    const reach = (RIBBON_REACH_PX - HAND_THROW.fwd) * (1 - Math.pow(1 - k, 2))
    const end = { x: hand.x + dir * reach, y: hand.y + 30 * k }
    ribbon = { end, retractFrom: -1 }
    return { hand, end, taut: 0 }
  }
  if (phase === 'active' && hitIndex === 0) {
    const hand = at(HAND_THROW)
    const end = grabbed ? chest : { x: boss.x + dir * RIBBON_REACH_PX, y: hand.y + 30 }
    ribbon = { end, retractFrom: -1 }
    return { hand, end, taut: grabbed ? 1 : 0 }
  }
  if (phase === 'comboGap' && hitIndex === 0) {
    const hand = at(HAND_PULL)
    if (grabbed) { ribbon = { end: chest, retractFrom: -1 }; return { hand, end: chest, taut: 1 } }
    if (!ribbon) return null
    if (ribbon.retractFrom < 0) ribbon.retractFrom = time
    const k = (time - ribbon.retractFrom) / RETRACT_S
    if (k >= 1) return null
    const e = 1 - Math.pow(1 - k, 2)
    const end = { x: ribbon.end.x + (hand.x - ribbon.end.x) * e, y: ribbon.end.y + (hand.y - ribbon.end.y) * e }
    return { hand, end, taut: 0.5 }
  }
  ribbon = null
  return null
}

/** 毎フレーム描く (ボスの絵の後)。藤色の帯が、ねじれて見えるよう幅を変えながら手から伸びる。張っている時は波を消す。 */
export function drawBladePullRibbon(ctx: CanvasRenderingContext2D, boss: Boss | null | undefined, player: Player, footY: number, time: number): void {
  if (!boss) return
  const r = bladePullRibbonEnds(boss, player, footY, time)
  if (!r) return
  const { hand, end, taut } = r
  const dx = end.x - hand.x
  const dy = end.y - hand.y
  const len = Math.hypot(dx, dy)
  if (len < 4) return
  const nx = -dy / len
  const ny = dx / len
  const N = Math.max(8, Math.round(len / 10))
  const wave = (1 - taut) * 14
  const left: Pt[] = []
  const right: Pt[] = []
  const mid: Pt[] = []
  for (let i = 0; i <= N; i++) {
    const s = i / N
    const off = wave * Math.sin(s * Math.PI * 2.5 - time * 14) * Math.sin(s * Math.PI)
    const cx = hand.x + dx * s + nx * off
    const cy = hand.y + dy * s + ny * off
    // ねじれ: 幅が太い・細いを繰り返す (細い所でも消えない)。
    const w = RIBBON_WIDTH_PX * (0.35 + 0.65 * Math.abs(Math.cos(s * Math.PI * 3 + time * 6)))
    left.push({ x: cx + nx * w / 2, y: cy + ny * w / 2 })
    right.push({ x: cx - nx * w / 2, y: cy - ny * w / 2 })
    mid.push({ x: cx, y: cy })
  }
  if (bladeSashPaintOn() && drawPaintedSash(ctx, mid, len, taut, time)) return
  ctx.save()
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.shadowColor = RIBBON_GLOW
  ctx.shadowBlur = 10
  ctx.fillStyle = RIBBON_EDGE
  ctx.beginPath()
  ctx.moveTo(left[0]!.x, left[0]!.y)
  for (const p of left) ctx.lineTo(p.x, p.y)
  for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i]!.x, right[i]!.y)
  ctx.closePath()
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.strokeStyle = RIBBON_CORE
  ctx.lineWidth = 1.6
  ctx.beginPath()
  ctx.moveTo(mid[0]!.x, mid[0]!.y)
  for (const p of mid) ctx.lineTo(p.x, p.y)
  ctx.stroke()
  ctx.restore()
}

/** 描いた絵 (11-01 既定・?bladeFxRest=now で前の描き方)：描いた腰の帯を、手から先までの曲線に沿って縦に細く切って並べる。
 *  不透明のまま重ねる (加算・光・白い芯を使わない)。ねじれは絵のしわ、波はコード。先は ほどけた端の絵。絵が無ければ false。 */
const SASH_OVERLAP = 2
function drawPaintedSash(ctx: CanvasRenderingContext2D, mid: Pt[], len: number, taut: number, time: number): boolean {
  const strip = bladeFxRestImg('blade-dancer/sash_strip')
  const tipDef = SASH_TIPS[taut >= 1 ? 1 : 0]
  const tip = bladeFxRestImg(`blade-dancer/${tipDef.name}`)
  if (!strip || !tip) return false
  const k = bladeSashWidth() / SASH_STRIP.band          // 絵 1px → 画面の px
  const drawH = SASH_STRIP.h * k
  // 先の端の絵は長いので横だけ詰める (布の幅は帯と同じ)。はためきは縦の伸び縮みで。
  const tipLen = Math.min(len * 0.32, 96)
  const bodyLen = len - tipLen
  // 帯の絵を手の側から使う。絵の横幅より長い時は伸ばす (届く長さ 440px なら絵の中に収まる)。
  const srcPerPx = Math.min(1 / k, strip.naturalWidth / Math.max(1, bodyLen))
  ctx.save()
  ctx.imageSmoothingEnabled = true
  let run = 0
  let tipAt: Pt | null = null
  let tipRad = 0
  for (let i = 0; i < mid.length - 1; i++) {
    const a = mid[i]!, b = mid[i + 1]!
    const seg = Math.hypot(b.x - a.x, b.y - a.y)
    if (seg <= 0) continue
    const rad = Math.atan2(b.y - a.y, b.x - a.x)
    if (run >= bodyLen) break
    const use = Math.min(seg, bodyLen - run)
    const sx = run * srcPerPx
    const sw = Math.max(1, use * srcPerPx)
    ctx.save()
    ctx.translate(a.x, a.y)
    ctx.rotate(rad)
    // 曲がりの外側に楔形の隙間 (縁の暗い点) が出ないよう、両端を 2px ずつ伸ばして隣と重ねる (11-01)。絵もその分だけ前後から取る。
    const padA = Math.min(SASH_OVERLAP, sx / srcPerPx), padB = SASH_OVERLAP
    const s0 = sx - padA * srcPerPx
    const s1 = Math.min(strip.naturalWidth, sx + sw + padB * srcPerPx)
    ctx.drawImage(strip, s0, 0, Math.max(1, s1 - s0), SASH_STRIP.h, -padA, -drawH / 2, (s1 - s0) / srcPerPx, drawH)
    ctx.restore()
    run += use
    tipAt = { x: a.x + Math.cos(rad) * use, y: a.y + Math.sin(rad) * use }
    tipRad = rad
  }
  if (tipAt && tipLen > 2) {
    const flutter = taut >= 1 ? 1 : 1 + 0.14 * Math.sin(time * 17)
    const sy = k * flutter
    ctx.translate(tipAt.x, tipAt.y)
    ctx.rotate(tipRad)
    ctx.drawImage(tip, -0.8, -tipDef.cutY * sy, tipLen + 0.8, tip.naturalHeight * sy)
  }
  ctx.restore()
  return true
}
